import { LimitExceededError } from './error'

import type { Database } from '~/apis/generated/database'
import { useOrganization } from '~/composables/useOrganization'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { LocationUpdateParams } from '~/entities/location'

const MAX_LOCATIONS = 300

export const getLocations = async (locationIds?: number[]) => {
  const query = useSupabaseClient<Database>()
    .from('locations')
    .select('*')
    .order('position', { ascending: true })
    .order('id', { ascending: true })
  if (locationIds && locationIds.length > 0) {
    query.in('id', locationIds)
  }
  const { data, error } = await query
  if (error) {
    throw error
  }
  return data
}

export const getLocation = async (locationId: number) => {
  const { data, error } = await useSupabaseClient<Database>()
    .from('locations')
    .select('*')
    .eq('id', locationId)
    .single()

  if (error) {
    throw error
  }
  return data
}

export const getLocationsCount = async (): Promise<number> => {
  const { count, error } = await useSupabaseClient<Database>()
    .from('locations')
    .select('id', { count: 'exact', head: true })
  if (error) throw error
  return count ?? 0
}

const getNextPosition = async (): Promise<number> => {
  const { data, error } = await useSupabaseClient()
    .from('locations')
    .select('position.max()')
    // 集計関数の結果が正しく認識されていないので、強制的に型を変換する
    // https://github.com/supabase/postgrest-js/issues/523
    .returns<{ max: number | null }[]>()
  if (error) throw error
  if (data[0].max === null) return 0
  return data[0].max + 1
}

export const createLocation = async (name: string): Promise<void> => {
  const { organization } = useOrganization()
  if (!organization.value) throw new Error('Organization is not set')
  const count = await getLocationsCount()
  if (count >= MAX_LOCATIONS) throw new LimitExceededError()
  const position = await getNextPosition()
  const { error } = await useSupabaseClient()
    .from('locations')
    .insert({ name, position, organization_id: organization.value.id })

  if (error) {
    throw error
  }
}

export const updateLocation = async (id: number, params: LocationUpdateParams) => {
  const { data, error } = await useSupabaseClient<Database>()
    .from('locations')
    .update(params)
    .eq('id', id)
    .select()
    .single()
  if (error) {
    throw error
  }
  return data
}

export const deleteLocation = async (id: number): Promise<void> => {
  const { error } = await useSupabaseClient().from('locations').delete().eq('id', id)
  if (error) {
    throw error
  }
}
